import React from 'react';
import Route from 'react-router/lib/Route';
import {
  JSON_PARSE_ERROR,
  DOMAIN_CHANGED_ERROR
} from './actions/foundry_response';
import { setAdsTargeted } from './actions/ads';
import { finishFetching, unsetInitialPageview } from './actions/api';
import fetchDataOnClient, { shouldFetch } from './utils/fetchDataOnClient';
import bootstrapVendorScripts from './initializers/bootstrap_vendor_scripts';
import setAdTargeting from './utils/ads/setAdTargeting';
import updateTrackingBeacons from './utils/updateTrackingBeacons';
import { renderingOnServer } from './utils/env';
import { reload } from './utils/dom';
import getRosettaContainer from './utils/getRosettaContainer';

import {each, compact, uniqueId, get} from "lodash";

const shimReactRouterState = (nextState, locationIsomorphicProps) => each([
  'hostname',
  'protocol'
], field => nextState.location[field] = locationIsomorphicProps[field]);

const updateLinkTrackingAndBeacons = (
  prevState,
  nextState,
  store,
  routerState,
  didFetch,
  lastFetchedLocation
) => {
  const state = store.getState();

  if (renderingOnServer()) {
    return;
  }

  const { isInitialPageview } = state.api;
  if (isInitialPageview) {
    bootstrapVendorScripts(store);
  }

  /*
   * For determining the previous location:
   * - When the route is updated and we visit a new page, `lastFetchedLocation`
   * tells us the previous location we were on.
   * - When the route is updated and we do not go to a new page, e.g. slideshows,
   * `prevState.location` tells us the previous location we were on.
   */
  const prevLoc = prevState ? prevState.location : lastFetchedLocation;

  updateTrackingBeacons({
    state: store.getState(),
    loc: nextState.location,
    prevLoc,
    beacons: compact([
      'google',
      'newsroom',
      !isInitialPageview && 'dfp',
      !isInitialPageview && 'parsely',
      didFetch && 'nielsen'
    ])
  });

  if (isInitialPageview || didFetch) {
    const adTargetingId = uniqueId();
    routerState.adTargetingId = adTargetingId;

    setAdTargeting(store, nextState.location).then(() => {
      if (routerState.adTargetingId !== adTargetingId) {
        /*
         * Don't mark ads targeted if the ad targeting from a
         * _previous route_ completed when the user has moved
         * on to another route.
         *
         * More details:
         * https://github.com/refinery29/rosetta/pull/2159#issuecomment-391726350
         */
        return;
      }
      store.dispatch(setAdsTargeted(true));
    });
  }
};

const surfaceErrorsToSentry = (fn) =>
  global.Raven
    ? global.Raven.wrap(fn)
    : fn;

const captureException = (error, callback) => {
  global.Raven && global.Raven.captureException(error);
  callback(error);
};

export default (store, locationIsomorphicProps) => {
  /*
   * State that changes as the user navigates Rosetta but
   * isn't used for render logic and thus shouldn't be in Redux
   */
  const routerState = {
    /*
     * What problem does this flag solve?
     * Check out this GitHub comment for the answer:
     * https://github.com/refinery29/rosetta/pull/2159#issuecomment-391726350
     */
    adTargetingId: null
  };

  const onEnterOrChange = surfaceErrorsToSentry((
    prevState,
    nextState,
    callback
  ) => {
    shimReactRouterState(nextState, locationIsomorphicProps);

    const shouldFetchValue = shouldFetch(nextState, store);
    const lastFetchedLocation = store.getState().api.lastFetchedLocation
      ? { ...store.getState().api.lastFetchedLocation }
      : null;
    const finishRoute = () => {
      updateLinkTrackingAndBeacons(
        prevState,
        nextState,
        store,
        routerState,
        shouldFetchValue,
        lastFetchedLocation
      );
      callback();
    };

    if (!shouldFetchValue) {
      finishRoute();
      return Promise.resolve(); // keep return value of this function consistent
    }

    /*
     * For SPA clicks, we want to flip `adTargetingCompleted` off then on again.
     *
     * This is because our ad components decide whether or not to refresh themselves
     * based on this prop.
     *
     * (Check out the invocation of `setAdTargeting` above to see where we flip it
     * back on.)
     *
     * It's important to dispatch this action _before_ the fetch action -- state
     * updates are ignored while we are fetching! See `ROUTING.md` for more
     * details about that.
     */
    if (window.htlbid && window.htlbid.newPage) {
      window.htlbid.newPage().then(() => {
        window.htlbid.forceRefresh();
      });
    }

    const { adTargetingCompleted } = store.getState().ads;
    const { isInitialPageview } = store.getState().api;
    if (!isInitialPageview && adTargetingCompleted) {
      store.dispatch(setAdsTargeted(false));
    }

    /*
     * The `fetchType` is passed into our Redux store, and is used to determine
     * which of our two types of spinners will show up (The global spinner
     * and the inline spinners).
     *
     * If you're on the same path as before, you're making an `fetchingOnSamePage`-type
     * fetch. If you're on a different path, you're making an `fetchingNewPage`-type
     * fetch.
     *
     * There's also a `moreContent`-type fetch, but it's triggered by clicking
     * the More Stories or More Products button.
     */
    const fetchType = !prevState ? 'fetchingNewPage' : 'fetchingOnSamePage';
    return fetchDataOnClient(nextState, fetchType, store)
      /*
       * Which cases go under `.then` and which go under `.catch`?
       *
       * If everything goes well, we go to `.then`.
       *
       * If the API returns an error status code, we swallow that up
       * in our Redux action logic, and set the state appropriately so that
       * we can render the green error page. Then, it goes to `.then`.
       *
       * If the API runs into a CORS issue, we enter the `.catch` block below.
       *
       * If the API returns a 2xx (including a redirect that ended up in a 2xx),
       * but something else goes wrong when hydrating the Redux state, we enter
       * the `.catch` block below.
       */
      .then(() => {
        const { api } = store.getState();
        if (api.hasErrors) {
          throw new Error(api.errorMessage);
        }
        return finishRoute();
      })
      .catch(error => {
        /*
         * Sometimes, the API request redirects on the Nginx side
         * (because of a redirect in the Redirect Tool, for example).
         * In this case, just refresh the page we're on, in order that
         * the browser hits Nginx and follows the redirect itself.
         */
        if (error === JSON_PARSE_ERROR || error === DOMAIN_CHANGED_ERROR) {
          return captureException(error, () => reload());
        }
        captureException(error, callback);
      });
  });

  const routeProps = {
    getComponent: (_nextState, callback) => {
      /*
       * The next lines look bad because we're mixing promises and callbacks.
       *
       * You _can_ return a promise to getComponent. However, when we attempted
       * to do that the first time, a small percentage of traffic started
       * timing out in Node, resulting in user-facing 504s.
       *
       * Any attempt to clean up this code should be load tested by using
       * the `ab` tool on an Openstack or Staging box.
       */
      getRosettaContainer(store)
        .then(
          container => {
          /*
           * The order in which we call `callback` and dispatch
           * `FINISH_FETCHING` is important. Read ROUTING.md
           * in order to understand why.
           */
            callback(null, container);
            if (store.getState().api.fetchStatus !== 'notFetching') {
              store.dispatch(finishFetching());
            }
          },
          error => {
            captureException(error, callback);
            if (store.getState().api.fetchStatus !== 'notFetching') {
              store.dispatch(finishFetching());
            }
          }
        );
    },
    onEnter: (nextState, replace, callback) => {
      return onEnterOrChange(null, nextState, callback);
    },
    onChange: (prevState, nextState, replace, callback) => {
      if (store.getState().api.isInitialPageview) {
        store.dispatch(unsetInitialPageview());
      }
      return onEnterOrChange(prevState, nextState, callback);
    },
    onLeave: () => {
      if (store.getState().api.isInitialPageview) {
        store.dispatch(unsetInitialPageview());
      }
    }
  };

  return <Route path="*" {...routeProps} />;
};
